import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { ArenaDef, GameDefinition, TilesetDef } from '@archon3d/core';
import { newGameDefinition, type CatalogGame } from './gameLibrary';
import { newBoardDef, hexRadiusOf } from './editorStore';
import { blankArena, resizeArena } from './arenaEditing';
import { ORIGINAL_GAME, ORIGINAL_HEX_GAME } from '@archon3d/content';
import { copyEditableGame } from './copyGame';
import { newId } from './newId';

function CreationDialog({
  title,
  onCancel,
  children,
}: {
  title: string;
  onCancel: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    ref.current?.showModal();
  }, []);
  return (
    <dialog
      ref={ref}
      className="creation-dialog"
      aria-label={title}
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <h2>{title}</h2>
      {children}
    </dialog>
  );
}

export function NewGameDialog({
  library,
  onCreate,
  onCancel,
}: {
  library: CatalogGame[];
  onCreate: (game: GameDefinition) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('My game');
  const [template, setTemplate] = useState('');
  const [grid, setGrid] = useState<'square' | 'hex'>('square');
  const [width, setWidth] = useState(9),
    [height, setHeight] = useState(9),
    [radius, setRadius] = useState(5);
  const source = library.find((g) => g.definition.id === template)?.definition;
  return (
    <CreationDialog title="New Game Board" onCancel={onCancel}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const game = source
            ? copyEditableGame(source, name.trim())
            : newGameDefinition(
                name.trim(),
                true,
                grid === 'hex' ? ORIGINAL_HEX_GAME : ORIGINAL_GAME,
              );
          if (!source)
            game.board = newBoardDef({
              name: game.name,
              grid,
              width,
              height,
              radius: grid === 'hex' ? radius : undefined,
              takenIds: [],
            });
          if (!source) game.board.cells = game.board.cells.map((r) => r.replace(/[LD]/g, '~'));
          game.board.id = game.id;
          game.board.name = game.name;
          onCreate(game);
        }}
      >
        <label>
          Game name
          <input
            autoFocus
            required
            maxLength={100}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          Template
          <select
            aria-label="Template"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
          >
            <option value="">None / empty</option>
            {library.map((g) => (
              <option key={g.definition.id} value={g.definition.id}>
                {g.definition.name} · {g.source}
              </option>
            ))}
          </select>
        </label>
        <label>
          Board grid
          <select
            aria-label="Board grid"
            value={source?.board.grid ?? (source ? 'square' : grid)}
            disabled={!!source}
            onChange={(e) => setGrid(e.target.value as 'square' | 'hex')}
          >
            <option value="square">Square</option>
            <option value="hex">Hexagon</option>
          </select>
        </label>
        {source ? (
          <p>
            {source.board.width} × {source.board.height} ·{' '}
            {source.board.grid === 'hex' ? `radius ${hexRadiusOf(source.board)} · ` : ''}
            {source.players.min}–{source.players.max} players. Includes all arenas and character
            abilities.
          </p>
        ) : grid === 'hex' ? (
          <label>
            Radius
            <input
              type="number"
              min={2}
              max={7}
              required
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
            />
          </label>
        ) : (
          <>
            <label>
              Width
              <input
                type="number"
                min={3}
                max={16}
                required
                value={width}
                onChange={(e) => setWidth(Number(e.target.value))}
              />
            </label>
            <label>
              Height
              <input
                type="number"
                min={3}
                max={16}
                required
                value={height}
                onChange={(e) => setHeight(Number(e.target.value))}
              />
            </label>
          </>
        )}
        <p className="hint">
          Grid and size are fixed after creation. Templates retain their original dimensions. Your
          current draft will be replaced; save it first if needed.
        </p>
        <div className="menu-actions">
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
          <button className="primary" disabled={!name.trim()}>
            Create game
          </button>
        </div>
      </form>
    </CreationDialog>
  );
}

export function NewArenaDialog({
  arenas,
  tilesets,
  initialTemplate = '',
  onCreate,
  onCancel,
}: {
  arenas: ArenaDef[];
  tilesets: TilesetDef[];
  initialTemplate?: string;
  onCreate: (arena: ArenaDef) => void;
  onCancel: () => void;
}) {
  const [template, setTemplate] = useState(initialTemplate);
  const [name, setName] = useState('My arena');
  const [width, setWidth] = useState(16),
    [height, setHeight] = useState(16);
  const source = arenas.find((a) => a.id === template);
  return (
    <CreationDialog title="New Arena" onCancel={onCancel}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const arena = source
            ? structuredClone(source)
            : resizeArena(blankArena(tilesets[0]!), tilesets[0]!, width, height);
          arena.id = `arena-${newId()}`;
          arena.name = name.trim();
          onCreate(arena);
        }}
      >
        <label>
          Arena name
          <input
            autoFocus
            required
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          Arena template
          <select
            aria-label="Arena template"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
          >
            <option value="">None / empty</option>
            {arenas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Width
          <input
            type="number"
            min={8}
            max={32}
            required
            disabled={!!source}
            value={source?.width ?? width}
            onChange={(e) => setWidth(Number(e.target.value))}
          />
        </label>
        <label>
          Height
          <input
            type="number"
            min={8}
            max={32}
            required
            disabled={!!source}
            value={source?.height ?? height}
            onChange={(e) => setHeight(Number(e.target.value))}
          />
        </label>
        <p className="hint">
          Arena size is fixed after creation. Templates keep their geometry and spawn positions.
        </p>
        <div className="menu-actions">
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
          <button className="primary" disabled={!name.trim()}>
            Create arena
          </button>
        </div>
      </form>
    </CreationDialog>
  );
}
